import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useAccountAuth } from '@/app/hooks/useAccountAuth';
import { accountAuthApi } from '@/services/endpoints/account/auth';
import { accountProfileApi } from '@/services/endpoints/account/organization';
import { getTimezoneOptions } from '@/utils/timezone-options';
import { ACCOUNT_USER_KEYS } from '@/components/account/queryKeys';
import {
  accountInputClass,
  DataRow,
  FieldLabel,
  LoadingRows,
  Panel,
  SectionTitle,
} from '@/components/account/accountUi';

function apiMessage(error: unknown, fallback: string): string {
  const axiosError = error as AxiosError<{ message?: string; error?: string }>;
  return axiosError?.response?.data?.message || axiosError?.response?.data?.error || fallback;
}

/** Reads a string field from the loosely-typed /my-profile payload. */
function readString(source: Record<string, unknown> | undefined, key: string): string {
  const value = source?.[key];
  return typeof value === 'string' ? value : '';
}


export default function AccountProfile() {
  const queryClient = useQueryClient();
  const { context, initials } = useAccountAuth();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [timezone, setTimezone] = useState('');

  const { data: profile, isLoading } = useQuery({
    queryKey: ACCOUNT_USER_KEYS.profile,
    queryFn: accountProfileApi.get,
    staleTime: 5 * 60_000,
  });

  const { data: timezoneData } = useQuery({
    queryKey: ACCOUNT_USER_KEYS.timezone,
    queryFn: accountAuthApi.getMyTimezone,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (!profile || nameTouched) return;
    setFirstName(readString(profile, 'firstName') || context?.employee?.firstName || '');
    setLastName(readString(profile, 'lastName') || context?.employee?.lastName || '');
    setMiddleName(readString(profile, 'middleName'));
  }, [profile, nameTouched, context]);

  useEffect(() => {
    if (timezoneData?.timezone) setTimezone(timezoneData.timezone);
  }, [timezoneData]);

  const timezoneOptions = useMemo(() => getTimezoneOptions(), []);

  const saveName = useMutation({
    mutationFn: () =>
      accountProfileApi.update({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        middleName: middleName.trim() ? middleName.trim() : null,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ACCOUNT_USER_KEYS.profile });
      setNameTouched(false);
      toast.success('Profile updated. Sign in again to refresh the name in the header.');
    },
    onError: (error) => toast.error(apiMessage(error, 'Could not save your profile.')),
  });

  const saveTimezone = useMutation({
    mutationFn: (value: string) => accountAuthApi.updateMyTimezone(value),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ACCOUNT_USER_KEYS.timezone });
      toast.success('Timezone updated.');
    },
    onError: (error) => toast.error(apiMessage(error, 'Could not update your timezone.')),
  });

  const email =
    readString(profile, 'workEmail') ||
    readString(profile, 'email') ||
    context?.user?.email ||
    '—';

  const handleNameSubmit = (event: FormEvent) => {
    event.preventDefault();
    saveName.mutate();
  };

  return (
    <>
      <SectionTitle title="Profile" description="How you appear across AI2Aim." />

      <div className="space-y-6">
        <Panel title="Your details">
          {isLoading ? (
            <LoadingRows rows={4} />
          ) : (
            <form onSubmit={handleNameSubmit} className="space-y-5">
              <div className="flex items-center gap-4">
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-brand-cyan/30 bg-brand-cyan/10 text-lg font-bold text-brand-cyan">
                  {initials}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-medium">{`${firstName} ${lastName}`.trim() || '—'}</p>
                  <p className="text-sm text-muted-foreground [overflow-wrap:anywhere]">{email}</p>
                </div>
              </div>

              <div className="grid gap-5 sm:grid-cols-3">
                <div>
                  <FieldLabel htmlFor="profile-first">First name</FieldLabel>
                  <input
                    id="profile-first"
                    className={accountInputClass}
                    value={firstName}
                    onChange={(event) => {
                      setNameTouched(true);
                      setFirstName(event.target.value);
                    }}
                    required
                  />
                </div>
                <div>
                  <FieldLabel htmlFor="profile-middle">Middle name</FieldLabel>
                  <input
                    id="profile-middle"
                    className={accountInputClass}
                    value={middleName}
                    onChange={(event) => {
                      setNameTouched(true);
                      setMiddleName(event.target.value);
                    }}
                  />
                </div>
                <div>
                  <FieldLabel htmlFor="profile-last">Last name</FieldLabel>
                  <input
                    id="profile-last"
                    className={accountInputClass}
                    value={lastName}
                    onChange={(event) => {
                      setNameTouched(true);
                      setLastName(event.target.value);
                    }}
                    required
                  />
                </div>
              </div>

              <Button type="submit" disabled={saveName.isPending || !nameTouched}>
                {saveName.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : null}
                Save profile
              </Button>
            </form>
          )}
        </Panel>

        <Panel
          title="Timezone"
          description="Dates and times across AI2Aim are shown in this zone."
        >
          <div className="flex flex-wrap items-end gap-3">
            {/* Capped at 100%: a bare 18rem minimum was wider than the panel's
                content box on a 360px phone (T101026002). */}
            <div className="min-w-[min(18rem,100%)] flex-1">
              <FieldLabel htmlFor="profile-timezone">Your timezone</FieldLabel>
              <select
                id="profile-timezone"
                className={accountInputClass}
                value={timezone}
                onChange={(event) => setTimezone(event.target.value)}
              >
                <option value="">Use organization default</option>
                {timezoneOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <Button
              onClick={() => saveTimezone.mutate(timezone)}
              disabled={saveTimezone.isPending || !timezone || timezone === timezoneData?.timezone}
            >
              {saveTimezone.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : null}
              Save timezone
            </Button>
          </div>
        </Panel>

        <Panel title="Account" bodyClassName="px-5 py-1">
          <dl>
            <DataRow label="Email" value={email} />
            <DataRow label="Organization" value={context?.organization?.name ?? '—'} />
            <DataRow
              label="Role"
              value={<span className="capitalize">{context?.role?.name ?? '—'}</span>}
            />
          </dl>
          <p className="pb-3 text-xs text-muted-foreground">
            Changing your sign-in email or role is handled by your organization owner in the
            employee management app.
          </p>
        </Panel>
      </div>
    </>
  );
}
